import React, { useId } from 'react';
import { X, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import './Modal.css';

/**
 * Reusable Modal component for displaying messages and errors, or rendering custom content
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {string} message - Modal message content (for simple message modals)
 * @param {ReactNode} children - Custom content to render (overrides message)
 * @param {string} type - Type of modal: 'info', 'success', 'error', 'warning'
 * @param {string} confirmText - Text for confirm button (default: 'OK')
 * @param {boolean} showFooter - Whether to show the OK button footer (default: true for message modals, false for children)
 */
const TYPE_ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle
};

const SIZES = ['small', 'medium', 'large', 'xlarge'];

const Modal = ({
  isOpen,
  onClose,
  title,
  message,
  children,
  type = 'info',
  confirmText = 'OK',
  showFooter,
  size = 'medium'
}) => {
  const titleId = useId();

  if (!isOpen) return null;

  // Info (and unknown types) keep a plain header; status types get a soft icon and accent line
  const TypeIcon = TYPE_ICONS[type];
  const sizeClass = SIZES.includes(size) ? size : 'medium';
  const dialogClass = [
    'ui-dialog',
    `ui-dialog--${sizeClass}`,
    TypeIcon ? `ui-dialog--${type}` : ''
  ].filter(Boolean).join(' ');

  // If children are provided, don't show footer by default
  const shouldShowFooter = showFooter !== undefined ? showFooter : !children;

  return (
    <div className="ui-dialog-overlay" onClick={onClose}>
      <div
        className={dialogClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ui-dialog-head">
          <div className="ui-dialog-heading">
            {TypeIcon && (
              <span className={`ui-dialog-icon ui-dialog-icon--${type}`} aria-hidden="true">
                <TypeIcon size={18} />
              </span>
            )}
            <h2 id={titleId} className="ui-dialog-title">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-dialog-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Render children if provided, otherwise render message */}
        <div className="ui-dialog-body">
          {children ? (
            children
          ) : (
            <div className="ui-dialog-message">
              {message}
            </div>
          )}
        </div>

        {/* Only show footer if needed */}
        {shouldShowFooter && (
          <div className="ui-dialog-foot">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-primary ui-dialog-ok"
            >
              {confirmText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
